import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import axios from "axios";
import MetalRateGrid, {
  buildCountryNameMap,
  dateColumn,
  filterMetalRateRows,
  withCountryName,
  countryColumn,
  karatColumn,
  metalNameColumn,
  purityColumn,
  ratePerGramColumn,
  rateTypeColumn,
  rateWithGstColumn,
  stateColumn,
  taxColumn,
  updatedByColumn,
} from "./MetalRateGrid";

const MetalRateHistory = ({
  bullionId,
  initialList,
  title = "Metal Rates History",
}) => {
  const legacyListRef = React.useRef(initialList || []);
  const containerRef = React.useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [list, setList] = useState(legacyListRef.current.slice(0, 10));
  const [total, setTotal] = useState(legacyListRef.current.length);
  const [loading, setLoading] = useState(false);
  const taxType = useSelector(
    (state) => state?.appConfig?.configData?.taxtype || "GST",
  );
  const { buyRateList = [], sellRateList = [], metals = [] } = useSelector(
    (state) => state.metalRate || {},
  );

  useEffect(() => {
    if (!containerRef.current || typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!bullionId || !shouldLoad) return;
    let cancelled = false;

    const loadHistory = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/v1/bullion-rates/getBullionRateHistory/${bullionId}`,
          { params: { page: page + 1, limit: pageSize } },
        );
        if (!cancelled) {
          setList(response?.data?.data?.rates || []);
          setTotal(response?.data?.data?.total || 0);
        }
      } catch {
        if (!cancelled && legacyListRef.current.length) {
          setList(
            legacyListRef.current.slice(
              page * pageSize,
              page * pageSize + pageSize,
            ),
          );
          setTotal(legacyListRef.current.length);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [bullionId, page, pageSize, shouldLoad]);

  const rows = useMemo(() => {
    const countryNames = buildCountryNameMap(
      list,
      buyRateList,
      sellRateList,
      metals,
    );
    return withCountryName(list, countryNames);
  }, [list, buyRateList, sellRateList, metals]);

  const filteredRows = useMemo(
    () => filterMetalRateRows(rows, search),
    [rows, search],
  );

  const columns = useMemo(
    () => [
      metalNameColumn,
      countryColumn,
      stateColumn,
      karatColumn(false),
      purityColumn,
      rateTypeColumn,
      ratePerGramColumn,
      taxColumn(taxType === "VAT" ? "TAX" : "GST"),
      rateWithGstColumn(taxType === "VAT" ? "Rate With Tax" : "Rate With GST"),
      dateColumn({
        field: "createdAt",
        headerName: "Valid From",
        dateFormat: "DD-MM-YYYY hh:mm A",
      }),
      dateColumn({
        field: "validTill",
        headerName: "Valid Till",
        dateFormat: "DD-MM-YYYY hh:mm A",
        fallback: "-",
      }),
      updatedByColumn,
    ],
    [taxType],
  );

  return (
    <Box ref={containerRef} sx={{ width: "100%" }}>
      <MetalRateGrid
        title={title}
        rows={search ? filteredRows : rows}
        columns={columns}
        loading={loading}
        search={search}
        onSearchChange={setSearch}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(0);
        }}
        rowCount={search ? filteredRows.length : total}
        paginationMode="server"
      />
    </Box>
  );
};

export default MetalRateHistory;
